import { Component, ChangeDetectionStrategy } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import { environment } from '@awo-care/shared-auth';

@Component({
  selector: 'app-footer',
  imports: [RouterModule, NgOptimizedImage],
  template: `
    <footer class="footer" role="contentinfo">
      <div class="footer-container">
        <div class="footer-main">
          <nav class="footer-section" aria-label="AWO Care Seiten" i18n-aria-label="@@footer.awoPages.ariaLabel">
            <h2 class="brand-name" i18n="@@footer.company">AWO Care</h2>
            <ul role="list">
              <li>
                <a routerLink="/info" i18n="@@footer.gettingStarted"
                  >Erste Schritte</a
                >
              </li>
              <li>
                <a routerLink="/schools" i18n="@@footer.schools"
                  >Teilnehmende Schulen</a
                >
              </li>
            </ul>
          </nav>

          <nav class="footer-section" aria-label="Rechtliche Informationen" i18n-aria-label="@@footer.legal.ariaLabel">
            <h2 i18n="@@footer.legal">Rechtliches</h2>
            <ul role="list">
              <li>
                <a routerLink="/legal/imprint" i18n="@@footer.imprint"
                  >Impressum</a
                >
              </li>
              <li>
                <a routerLink="/legal/privacy-policy" i18n="@@footer.dataPrivacy"
                  >Datenschutz</a
                >
              </li>
              <li>
                <a routerLink="/legal/cookies" i18n="@@footer.cookies"
                  >Cookies</a
                >
              </li>
              <li>
                <a routerLink="/barrierefreiheit" i18n="@@footer.accessibility"
                  >Barrierefreiheit</a
                >
              </li>
            </ul>
          </nav>

          <nav class="footer-section" aria-label="Support und Hilfe" i18n-aria-label="@@footer.support.ariaLabel">
            <h2 i18n="@@footer.support">Support</h2>
            <ul role="list">
              @if (!isProduction) {
              <li>
                <a routerLink="/faq" i18n="@@footer.faq"
                  >Häufige Fragen</a
                >
              </li>
              }
              <li>
                <a routerLink="/contact" i18n="@@footer.contact">Kontakt</a>
              </li>
            </ul>
          </nav>

          <div class="footer-section footer-about">
            <div class="footer-partner">
              <span i18n="@@footer.inPartnership" class="partnership-text"
                >im Auftrag von</span
              >
              <a
                href="https://www.awo-hs.org"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="AWO Hessen-Süd Website (öffnet in neuem Tab)"
                i18n-aria-label="@@footer.awoLink.ariaLabel"
              >
                <img
                  ngSrc="shared-assets/icons/awo_hs_logo.svg"
                  alt="AWO Hessen-Süd Logo"
                  i18n-alt="@@footer.awoLogo.alt"
                  width="150"
                  height="48"
                  class="partner-logo"
                  priority="low"
                />
              </a>
            </div>
            <!-- W3C-Konformitätslogo (#1192): Selbstauskunft, Geltungsbereich steht in der Barrierefreiheitserklärung -->
            <a
              href="https://www.w3.org/WAI/WCAG2AA-Conformance"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Konformität mit Stufe AA, W3C Web Content Accessibility Guidelines 2.1 – Erläuterung beim W3C (öffnet in neuem Tab)"
              i18n-aria-label="@@footer.wcagLogo.ariaLabel"
              title="Erläuterung der WCAG 2 Konformitätsstufe AA"
              i18n-title="@@footer.wcagLogo.title"
              class="wcag-logo"
            >
              <img
                ngSrc="wcag2.1AA-blue.svg"
                alt="Konformität mit Stufe AA, W3C Web Content Accessibility Guidelines 2.1"
                i18n-alt="@@footer.wcagLogo.alt"
                width="88"
                height="31"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        margin-top: auto;
      }

      .footer {
        background: #f8f8f8;
        border-top: 1px solid #e0e0e0;
        margin-top: 4rem;
      }

      .footer-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 2rem;
      }

      .footer-main {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 3rem;
        padding: 3rem 0;

        @media (min-width: 769px) {
          grid-template-columns: 1fr 1fr 1fr 2fr;
        }
      }

      .footer-section {
        h2 {
          font-size: 0.95rem;
          font-weight: 500;
          color: #333;
          margin: 0 0 1rem 0;

          &.brand-name {
            white-space: nowrap;
          }
        }

        ul {
          list-style: none;
          padding: 0;
          margin: 0;

          li {
            margin-bottom: 0.75rem;

            a {
              color: var(--awo-text-muted, #6f6f6f);
              text-decoration: none;
              font-size: 0.9rem;
              font-weight: 400;
              transition: color 0.2s ease;

              &:hover {
                color: var(--theme-primary);
              }
            }
          }
        }
      }

      .footer-about {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 1.5rem;

        .wcag-logo {
          display: inline-flex;
        }

        .footer-partner {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.5rem;

          .partnership-text {
            color: #666;
            font-size: 0.9rem;
            font-weight: 400;
          }

          a {
            display: flex;
            align-items: center;
          }

          .partner-logo {
            height: 48px;
            width: auto;
            opacity: 0.7;
            filter: grayscale(100%);
            transition: all 0.3s ease;

            &:hover {
              opacity: 1;
              filter: grayscale(0%);
            }
          }
        }
      }

      @media (max-width: 768px) {
        .footer-container {
          padding: 0 1rem;
        }

        .footer-main {
          grid-template-columns: 1fr;
          gap: 2rem;
          padding: 2rem 0;
        }

        .footer-about {
          align-items: center;
        }

        .footer-about .footer-partner {
          align-items: center;
          text-align: center;

          .partner-logo {
            height: 36px;
          }
        }
      }
    `,
  ],
})
export class FooterComponent {
  readonly isProduction = environment.production;
}
